import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, fetchAuthSession, signOut } from 'aws-amplify/auth';
import PasswordStrengthIndicator from '../../components/passwordStrengthIndicator';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import Preloader from '../../components/Preloader/Preloader';
import { Helmet } from 'react-helmet-async';

const isNumberRegx = /\d/;
const specialCharacterRegx = /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
const capsRegx = /[A-Z ]+/;

export default function Login() {
  const { validateLogin, loginValidated, setLoginValidated, loggedUser } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [loaded, setLoaded] = useState(true);
  const [passwordValidity, setPasswordValidity] = useState({
    minChar: null,
    number: null,
    specialChar: null,
    capsChar: null
  });
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [newpasswordFocused, setNewPasswordFocused] = useState(false);
  const [newpasswordValidity, setNewPasswordValidity] = useState({
    minChar: null,
    number: null,
    specialChar: null,
    capsChar: null
  });

  useEffect(() => {
    let mounted = true;

    const checkActiveSession = async () => {
      if (loggedUser.isAuthenticated) {
        navigate('/');
        return;
      }

      setLoaded(false);
      try {
        const session = await fetchAuthSession();
        if (session?.tokens?.idToken) {
          const isValid = await validateLogin(session);
          if (isValid && mounted) {
            enqueueSnackbar('Welcome back!', { variant: 'info' });
            navigate('/');
            return;
          }
        }
      } catch (error) {
        // No active session or session expired - allow clean login
      } finally {
        if (mounted) {
          setLoaded(true);
        }
      }
    };

    checkActiveSession();

    return () => {
      mounted = false;
    };
  }, [loggedUser.isAuthenticated]);

  const handleLogin = async (event) => {
    if (event) event.preventDefault();
    const trimmedUser = (username || '').trim();
    if (!trimmedUser || !password) {
      enqueueSnackbar('Username and Password cannot be empty', { variant: 'info' });
      return;
    }

    try {
      setLoaded(false);
      setPasswordFocused(false);
      setNewPasswordFocused(false);

      // Clean up any stale session before initiating a fresh sign-in
      const existingSession = await fetchAuthSession().catch(() => null);
      if (existingSession?.tokens?.idToken) {
        const existingEmail = existingSession.tokens.idToken.payload?.email || '';
        if (existingEmail.toLowerCase() === trimmedUser.toLowerCase()) {
          const isValid = await validateLogin(existingSession);
          if (isValid) {
            enqueueSnackbar('You are already signed in.', { variant: 'info' });
            navigate('/');
            return;
          }
        }
        await signOut().catch(() => {});
      }

      // Perform Cognito sign in
      const signInResult = await signIn({ username: trimmedUser, password });
      console.log('Sign in result:', signInResult);

      // Handle First-Time User Challenge (New Password Required)
      if (!signInResult.isSignedIn && signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setShowNewPass(true);
        enqueueSnackbar('First-time login: Please set up your new permanent password.', { variant: 'info' });
        setLoaded(true);
        return;
      }

      if (!signInResult.isSignedIn && signInResult.nextStep?.signInStep !== 'DONE') {
        enqueueSnackbar(`Verification required: ${signInResult.nextStep?.signInStep}`, { variant: 'info' });
        setLoaded(true);
        return;
      }

      // Successful sign in
      const session = await fetchAuthSession({ forceRefresh: true });
      const isValid = await validateLogin(session);
      if (isValid) {
        enqueueSnackbar('Signed in successfully!', { variant: 'success' });
        navigate('/');
      } else {
        enqueueSnackbar('User account is authenticated but not authorized for this library portal.', { variant: 'warning' });
      }
      setLoaded(true);
    } catch (signInError) {
      console.error('Sign in error:', signInError);
      setLoaded(true);

      if (signInError?.message?.toLowerCase().includes('already a signed in user')) {
        await signOut().catch(() => {});
        enqueueSnackbar('Cleared previous session. Please click Sign In again.', { variant: 'info' });
      } else if (signInError?.message?.toLowerCase().includes('incorrect username or password')) {
        enqueueSnackbar('Incorrect username or password. Please verify your credentials.', { variant: 'warning' });
      } else {
        enqueueSnackbar('Sign in failed: ' + (signInError?.message || signInError), { variant: 'warning' });
      }
    }
  };

  const handleChangePassword = async (event) => {
    if (event) event.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      enqueueSnackbar('New password must be at least 8 characters long.', { variant: 'warning' });
      return;
    }

    try {
      setLoaded(false);
      setNewPasswordFocused(false);

      const confirmResult = await confirmSignIn({
        challengeResponse: newPassword
      });
      console.log('Confirm sign in result:', confirmResult);

      if (confirmResult.isSignedIn || confirmResult.nextStep?.signInStep === 'DONE') {
        const session = await fetchAuthSession({ forceRefresh: true });
        const isValid = await validateLogin(session);
        if (isValid) {
          enqueueSnackbar('Password set successfully! Welcome to the library portal.', { variant: 'success' });
          navigate('/');
        } else {
          enqueueSnackbar('Password created successfully, but your role is not authorized.', { variant: 'warning' });
        }
      } else if (confirmResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        enqueueSnackbar('Password does not meet required complexity. Please try a stronger password.', { variant: 'warning' });
      } else {
        enqueueSnackbar(`Next step: ${confirmResult.nextStep?.signInStep || 'Action required'}`, { variant: 'info' });
      }
      setLoaded(true);
    } catch (error) {
      console.error('Change password error:', error);
      setLoaded(true);
      enqueueSnackbar('Error setting password: ' + (error.message || error), { variant: 'warning' });
    }
  };

  const onChangePassword = password => {
    setPassword(password);
    setPasswordValidity({
      minChar: password.length >= 8 ? true : false,
      number: isNumberRegx.test(password) ? true : false,
      specialChar: specialCharacterRegx.test(password) ? true : false,
      capsChar: capsRegx.test(password) ? true : false
    });
  };

  const onNewChangePassword = password => {
    setNewPassword(password);
    setNewPasswordValidity({
      minChar: password.length >= 8 ? true : false,
      number: isNumberRegx.test(password) ? true : false,
      specialChar: specialCharacterRegx.test(password) ? true : false,
      capsChar: capsRegx.test(password) ? true : false
    });
  };

  return (
    <div className='p-3'>
      <Helmet>
        <title>Sign In - Library - Vidyajyoti College of Theology - Delhi || Institute of Religious Studies</title>
        <meta name="description" content="Sign In - Library - Vidyajyoti College of Theology - Delhi || Institute of Religious Studies" />
        <meta name="keywords" content="Sign In - Library - Vidyajyoti College of Theology - Delhi || Institute of Religious Studies" />
      </Helmet>

      {!loaded && <Preloader show={loaded ? false : true} />}
      <div className='container card justify-content-center' style={{ borderRadius: "30px" }}>
        <div className="text-center p-3">
          <div className='text-center'>
            <h1>{showNewPass ? 'Set New Password' : 'Sign In'}</h1>
            {showNewPass && (
              <p className="text-muted small mt-1">
                First-time login: Please create a new permanent password for your account.
              </p>
            )}
          </div>
          <div className="contact-form text-center m-3" >
            <div className="row d-flex justify-content-center p-2">
              <div className="col-lg-2 col-md-12 col-sm-12">
                <label>Username</label>
              </div>
              <div className="col-lg-6 col-md-12 col-sm-12">
                <input name="email" type="email" className="form-control" id="email" placeholder="Email Address" required
                  onChange={event => setUsername(event.target.value)} value={username} disabled={showNewPass} />
              </div>
            </div>
            {!showNewPass ? <div className="row d-flex justify-content-center p-2">
              <div className="col-lg-2 col-md-12 col-sm-12">
                <label>Password</label>
              </div>
              <div className="col-lg-6 col-md-12 col-sm-12">
                <input name="password" type="password" className="form-control" id="password" placeholder="Password" required
                  onFocus={() => setPasswordFocused(true)}
                  onChange={e =>
                    onChangePassword(e.target.value)}
                  value={password} />
              </div>
            </div> : <div className="row d-flex justify-content-center p-2">
              <div className="col-lg-2 col-md-12 col-sm-12">
                <label>New Password</label>
              </div>
              <div className="col-lg-6 col-md-12 col-sm-12">
                <input name="password" type="password" className="form-control" id="password" placeholder="Enter New Password (min 8 characters)" required
                  onFocus={() => setNewPasswordFocused(true)}
                  onChange={e =>
                    onNewChangePassword(e.target.value)}
                  value={newPassword} />
              </div>
            </div>}
            {passwordFocused && (
              <PasswordStrengthIndicator
                validity={passwordValidity}
              />
            )}
            {newpasswordFocused && (
              <PasswordStrengthIndicator
                validity={newpasswordValidity}
              />
            )}

            <div className="row d-flex justify-content-center ">
              {!showNewPass && (
                <div>
                  <Link to="/forgotpassword">
                    Forgot password ?
                  </Link>
                </div>
              )}
              {!showNewPass ? <div className="col-lg-12  text-center">
                <a>
                  <button type="submit" className="btn-box mt-3" onClick={event => handleLogin(event)}>Sign In</button>
                </a>
              </div> : <div className="col-lg-12  text-center">
                <fieldset>
                  <button type="submit" className="btn-box mt-3" onClick={event => handleChangePassword(event)}>Set New Password & Sign In</button>
                </fieldset>
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-secondary"
                    onClick={() => {
                      setShowNewPass(false);
                      setNewPassword('');
                      setPassword('');
                    }}
                  >
                    ← Back to Sign In
                  </button>
                </div>
              </div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
